import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Mail, Shield, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PaceNudges from "@/components/pace/PaceNudges";
import AskPaceInline from "@/components/pace/AskPaceInline";
import { AITimesheetTriggerButton } from "@/components/pace/AITimesheetModal";
import HoursSummaryStrip from "@/components/hours/HoursSummaryStrip";
import { usePaceStatus } from "@/hooks/use-pace-status";

export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [counts, setCounts] = useState({ users: 0, invites: 0 });
  const { paceEnabled } = usePaceStatus();

  // Phase 24 — This week's range (Sat-Fri) in tenant tz.
  const weekRange = (() => {
    const today = new Date();
    // Approx "start of week" = last Saturday (weekday 6). Client-side approx;
    // the server uses tenant tz for the actual bucketing.
    const day = today.getDay();
    const offset = (day + 1) % 7;  // days since previous Saturday
    const start = new Date(today);
    start.setDate(today.getDate() - offset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(start), to: iso(end) };
  })();

  useEffect(() => {
    (async () => {
      try {
        const t = await api.get("/tenant/me");
        setTenant(t.data);
      } catch (_e) {}
      if (hasRole(["admin", "tenant_admin", "master"])) {
        try {
          const u = await api.get("/tenant/users");
          setCounts((c) => ({ ...c, users: u.data.items.length }));
        } catch (_e) {}
      }
      if (hasRole(["admin", "tenant_admin"])) {
        try {
          const i = await api.get("/tenant/invites");
          setCounts((c) => ({ ...c, invites: i.data.items.filter((x) => !x.redeemed_at).length }));
        } catch (_e) {}
      }
    })();
  }, [hasRole]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Overview</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
          Welcome, {user?.full_name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-2 text-muted-foreground" data-testid="dashboard-tenant-line">
          {tenant ? (
            <>You're in <span className="font-medium text-primary">{tenant.name}</span> · <span data-testid="dashboard-tenant-tz">{tenant.timezone}</span></>
          ) : (
            "Loading your workspace…"
          )}
        </p>
      </div>

      <PaceNudges />

      <AskPaceInline paceEnabled={paceEnabled} />

      <div data-testid="dashboard-this-week-summary" className="space-y-3">
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">This week</div>
        <HoursSummaryStrip from={weekRange.from} to={weekRange.to} />
      </div>

      {paceEnabled && (
        <div className="flex justify-end" data-testid="dashboard-ai-timesheet-slot">
          <AITimesheetTriggerButton />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {hasRole(["admin", "tenant_admin", "master"]) && (
          <StatCard icon={Users} label="Team members" value={counts.users} to="/app/directory" testid="stat-users" />
        )}
        {hasRole(["admin", "tenant_admin"]) && (
          <StatCard icon={Mail} label="Open invites" value={counts.invites} to="/app/admin/invites" testid="stat-invites" />
        )}
        <StatCard
          icon={Shield}
          label="MFA"
          value={user?.mfa_enabled ? "On" : "Off"}
          to="/app/settings/profile"
          testid="stat-mfa"
        />
        {hasRole(["master"]) && (
          <StatCard icon={Building2} label="Workspaces" value="View all" to="/master/tenants" testid="stat-tenants" />
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>What's ready to go</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Item done>Sign in from anywhere with MFA</Item>
          <Item done>Roles: worker, approver, admin, platform admin</Item>
          <Item done>Team directory across workspaces</Item>
          <Item>Kiosk PIN for worksite tablets</Item>
          <Item>Clock in, timesheets, and approvals</Item>
          <Item>Send hours to bill, AP, and payroll</Item>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, to, testid }) {
  return (
    <Card className="card-elevate transition-colors hover:border-secondary/40" data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-2 font-display text-2xl font-semibold text-primary">{value}</div>
          </div>
          <div className="rounded-xl bg-accent p-3 text-secondary">
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <Button asChild variant="link" className="mt-2 h-auto p-0 text-secondary">
          <Link to={to}>Open <ArrowRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function Item({ children, done }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`mt-1 h-2 w-2 rounded-full ${done ? "bg-success" : "bg-muted-foreground/40"}`} />
      <div className={done ? "text-foreground" : "text-muted-foreground"}>{children}</div>
    </div>
  );
}
